/**
 * 新闻舱「怎么借这条爆款」三选一(2026-10-10 追爆出口原话复用 §4.1)。
 *
 *   借热度写 CITE   —— 引用它的原话当由头,注明出处,正文讲你家(体裁:企业新闻)
 *   仿它的说法 STYLE —— 标题/导语套它的句式,事实全换成你家的(默认;体裁按用途)
 *   只学结构 STRUCTURE —— 原话不进稿,只学段落顺序和基调(体裁按用途)
 *
 * 逐句来自 C1(与数字人同一份逐句分析);拿不到逐句时只给 STRUCTURE。
 * 纯展示 + 回调,状态与上送(C3 `borrow`)在创建页。
 */
import { AlertTriangle, Loader2 } from "lucide-react";

import { LINE_ROLE_LABEL, type BlueprintLine } from "@/entities/explosive/model/blueprintLines";
import { BORROW_MODE_META, type BorrowMode } from "@/entities/explosive/model/reporterBorrow";
import { cn } from "@/shared/lib/utils";

const MODES: BorrowMode[] = ["CITE", "STYLE", "STRUCTURE"];

function LinePickList({
  lines,
  picked,
  onToggle,
  disabled,
  testIdPrefix,
}: {
  lines: BlueprintLine[];
  picked: string[];
  onToggle: (text: string) => void;
  disabled?: boolean;
  testIdPrefix: string;
}) {
  return (
    <ul className="space-y-1">
      {lines.map((l, i) => (
        <li key={l.idx}>
          <label className="flex cursor-pointer items-start gap-2 rounded-md px-1.5 py-1 text-xs leading-relaxed hover:bg-muted/60">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              data-testid={`${testIdPrefix}-${i}`}
              checked={picked.includes(l.text)}
              disabled={disabled}
              onChange={() => onToggle(l.text)}
            />
            <span className="mt-0.5 shrink-0 rounded border border-border bg-muted px-1 text-[10px] text-muted-foreground">
              {LINE_ROLE_LABEL[l.role]}
            </span>
            <span className="min-w-0 flex-1">{l.text}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

export function BorrowPicker({
  linesStatus,
  lines,
  mode,
  onMode,
  quotes,
  onToggleQuote,
  attribution,
  onAttribution,
  styleLines,
  onToggleStyle,
  disabled,
}: {
  /** loading=C1 进行中;ready=有逐句;unavailable=无口播/C1 失败(只给 STRUCTURE)。 */
  linesStatus: "loading" | "ready" | "unavailable";
  lines: BlueprintLine[];
  mode: BorrowMode;
  onMode: (m: BorrowMode) => void;
  quotes: string[];
  onToggleQuote: (text: string) => void;
  attribution: string;
  onAttribution: (v: string) => void;
  styleLines: string[];
  onToggleStyle: (text: string) => void;
  disabled?: boolean;
}) {
  const linesReady = linesStatus === "ready" && lines.length > 0;

  return (
    <section
      data-testid="reporter-borrow"
      className="space-y-3 rounded-[calc(var(--radius)+4px)] border bg-card p-3 sm:p-4"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-semibold">怎么借这条爆款?</h3>
        {linesStatus === "loading" && (
          <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            正在逐句分析参考片口播…
          </span>
        )}
        {linesStatus === "unavailable" && (
          <span className="text-2xs text-muted-foreground">
            参考片口播逐句没拿到(或本来就没口播)—— 这次只能学结构
          </span>
        )}
      </div>

      <div role="radiogroup" aria-label="怎么借这条爆款" className="grid gap-1.5 sm:grid-cols-3">
        {MODES.map((m) => {
          const meta = BORROW_MODE_META[m];
          const needsLines = m !== "STRUCTURE";
          const off = disabled || (needsLines && !linesReady);
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              data-testid={`reporter-borrow-${m}`}
              disabled={off}
              onClick={() => onMode(m)}
              className={cn(
                "rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                mode === m
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              )}
            >
              <div
                className={cn(
                  "text-xs font-semibold",
                  mode === m ? "text-primary" : "text-foreground"
                )}
              >
                {meta.label}
                {m === "STYLE" && <span className="ml-1 font-normal opacity-70">(默认)</span>}
              </div>
              <div className="mt-0.5 text-2xs leading-relaxed text-muted-foreground">
                {meta.desc}
              </div>
              <div className="mt-0.5 text-[10px] text-muted-foreground/80">→ {meta.genreHint}</div>
            </button>
          );
        })}
      </div>

      {mode === "CITE" && linesReady && (
        <div className="space-y-2 border-t pt-2.5">
          <div className="text-2xs font-medium text-foreground">引用哪句(导语里带出处引用)</div>
          <LinePickList
            lines={lines}
            picked={quotes}
            onToggle={onToggleQuote}
            disabled={disabled}
            testIdPrefix="reporter-borrow-quote"
          />
          <label className="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
            出处写法
            <input
              aria-label="出处写法"
              value={attribution}
              disabled={disabled}
              onChange={(e) => onAttribution(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-foreground"
            />
          </label>
          <p className="text-2xs text-muted-foreground">
            引用句里的数字只在带出处的引号里出现;正文讲的事实只取你写的素材。
          </p>
        </div>
      )}

      {mode === "STYLE" && linesReady && (
        <div className="space-y-2 border-t pt-2.5">
          <div className="text-2xs font-medium text-foreground">
            要仿的句子(标题/导语套它的句式)
          </div>
          <LinePickList
            lines={lines}
            picked={styleLines}
            onToggle={onToggleStyle}
            disabled={disabled}
            testIdPrefix="reporter-borrow-style"
          />
          <p className="flex items-start gap-1 text-2xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            句中数字/品牌不会当成你的事实 —— 成稿里出现会被标成高风险提醒。
          </p>
        </div>
      )}

      {mode === "STRUCTURE" && (
        <p className="border-t pt-2.5 text-2xs text-muted-foreground">
          原话一个字不进稿,只学上面的段落顺序和情绪基调。
        </p>
      )}
    </section>
  );
}
